import { SubmitHandler, UseFormSetValue } from "react-hook-form";
import TransferForm from "../types/TransferForm";
import { TransferDirectionEnum } from "../enums/TransferDirectionEnum";
import { mapFormValuesToTransfer } from "../utils/mapFormValuesToTransfer";
import Transfer from "../types/Transfer";
import { Dispatch, SetStateAction } from "react";
import useTransferPost from "./useTransferPost";
import useTranslate from "../../core/languages/hooks/useTranslate";

interface UseTransferSubmitHandlerParams {
    customer: any; // CustomerContextType
    setShowAlert: Dispatch<SetStateAction<boolean>>;
    setAlertMessage: Dispatch<SetStateAction<string>>;
    setAlertType: Dispatch<SetStateAction<"default" | "warning" | "info" | "success" | "error">>;
    reset: any; // UseFormReset
    setValue: UseFormSetValue<TransferForm>;
    setError: any; // UseFormSetError
    eventCity?: any;
    event?: any;
    city?: any;
    department?: any;
    profile?: any;
    setShowForm: Dispatch<SetStateAction<boolean>>;
}

/**
 * Hook to handle transfer form submission
 * Contains roundtrip logic, mapping and error handling
 */
export default function useTransferSubmitHandler({
    customer,
    setShowAlert,
    setAlertMessage,
    setAlertType,
    reset,
    setValue,
    setError,
    eventCity,
    event,
    city,
    department,
    profile,
    setShowForm,
}: UseTransferSubmitHandlerParams): SubmitHandler<TransferForm> {
    
    const submitTransferData = useTransferPost();
    const t = useTranslate();
    
    const restoreDefaultValues = () => {
        // Restore event-related values
        if (eventCity) { setValue("eventCity", eventCity.id); }
        if (event) { setValue("event", event.id); }
        if (city) { setValue("city", city.id); }
        if (department) { setValue("department", department.id); }
        
        // Restore profile-related values
        if (profile) {
            setValue("passengerName", profile?.firstName);
            setValue("passengerLastName", profile?.lastName);
            setValue("passengerContactEmail", profile?.email);
            setValue("passengerPhoneNumber", profile?.phoneNumber);
            setValue("invoiceFullName", profile?.invoiceFullName);
            setValue("invoiceAddress", profile?.invoiceAddress);
            setValue("nationality", profile?.nationality);
            setValue("documentType", profile?.documentType);
            setValue("invoicePassportCif", profile?.documentNumber);
            setValue("dialCode", profile?.dialCode ? `${profile.iso2}-${profile.dialCode}` : "");
        }
    };
    
    const handleSubmitSuccess = () => {
        const successMessage = t("transfers.message.succesCreatingRequestTransfer");
        const paymentMessage = t("transfers.message.paymentEmailNotice");
        const confirmationShortly = t("transfers.message.confirmationShortly");
        const changesMessage = t("transfers.message.anyChanges", {
            email: customer.customer.transferEmail ?? customer.customer.email
        });

        setAlertType("success");
        if (department.name?.toLowerCase() === "individuals" || department.name?.toLowerCase() === "individual"){
            setAlertMessage(`${successMessage}\n${paymentMessage}\n${changesMessage}`);
        }else{
            setAlertMessage(`${successMessage}\n${confirmationShortly}\n${changesMessage}`);
        }
        // Combine messages with clear visual separation
        setShowAlert(true);
        setShowForm(false);
        // Clear the form
        reset();
        
        // Restore default values after reset
        setTimeout(() => {
            restoreDefaultValues();
        }, 0);

        // Scroll up to see the message
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }, 100);
    };

    const handleSubmitError = (error: any) => {
        if (error.statusCode === 422) {
            setAlertMessage(t("common.feedback.form_not_valid"));
            error.content.violations.forEach((v => {
                setError(v.propertyPath, {message: v.message});
            }));
        } else {
            setAlertMessage(t("common.feedback.unexpected_error"));
        }
        setAlertType("error");
        setShowAlert(true);
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }, 100);
    };

    return (formData: TransferForm) => {
        try {
            const direction = formData.direction;

            // 🔹 ROUND TRIP: Create departure first, then arrival
            if (direction === TransferDirectionEnum.ROUND_TRIP) {
                const departureMapped = mapFormValuesToTransfer(formData, TransferDirectionEnum.DEPARTURE, customer.languageId);
                
                // Use promise wrapper to handle async submit
                submitTransferData.save(departureMapped as Transfer, {
                    onSuccess: (departureRes: Transfer) => {
                        // Extract the ID from departure
                        const departureId = departureRes['@id'] || `/transfers/${departureRes.uuid}`;

                        if (!departureId) {
                            setAlertType("error");
                            setAlertMessage(t("common.error"));
                            setShowAlert(true);
                            return;
                        }

                        // Create the arrival with reference to departure
                        const arrivalMapped = mapFormValuesToTransfer(formData, TransferDirectionEnum.ARRIVAL, customer.languageId, departureId);
                        
                        submitTransferData.save(arrivalMapped as Transfer, {
                            onSuccess: () => {
                                handleSubmitSuccess();
                            },
                            onError: (error: Error) => {
                                handleSubmitError(error);
                            }
                        });
                    },
                    onError: (error: Error) => {
                        handleSubmitError(error);
                    }
                });
            } else {
                // 🔹 ARRIVAL OR DEPARTURE TRIP: Create the transfer directly
                const singleMapped = mapFormValuesToTransfer(formData, direction as TransferDirectionEnum, customer.languageId);
                
                submitTransferData.save(singleMapped as Transfer, {
                    onSuccess: () => {
                        handleSubmitSuccess();
                    },
                    onError: (error: any) => {
                        handleSubmitError(error);
                    }
                });
            }
        } catch (error) {
            setAlertType("error");
            setAlertMessage(t("common.error"));
            setShowAlert(true);
        }
    };
}

